<template>
  <transition name="kn-fade">
    <div v-if="show" class="km-notif" :class="{ 'km-notif--lottery': isLotteryPage }">
      <button class="km-notif__btn" :class="{ active: open }" @click="toggle" aria-label="Notifications">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        <span v-if="unread" class="km-notif__badge">{{ unreadLabel }}</span>
      </button>

      <!-- Backdrop + dropdown -->
      <transition name="kn-drop">
        <div v-if="open" class="km-notif__mask" @click.self="close">
          <div class="km-notif__panel" :class="{ 'km-notif__panel--mobile': isMobile }">
            <header class="km-notif__head">
              <h3>Notifications <span v-if="unread" class="km-notif__count">{{ unread }}</span></h3>
              <div class="km-notif__head-actions">
                <button v-if="items.length" class="km-notif__mark" @click="markAllRead">Mark all read</button>
                <button class="km-notif__close" @click="close" aria-label="Close">✕</button>
              </div>
            </header>

            <div class="km-notif__body">
              <!-- Loading -->
              <div v-if="loading" class="km-notif__loading">
                <div class="km-notif__loader"></div>
                <span>Loading…</span>
              </div>

              <!-- Empty -->
              <div v-else-if="items.length === 0" class="km-notif__empty">
                <div class="km-notif__empty-emoji">🔔</div>
                <h4>You're all caught up</h4>
                <p>New notices will appear here.</p>
              </div>

              <!-- List -->
              <ul v-else class="km-notif__list">
                <li
                  v-for="(n, i) in items" :key="n.id || i"
                  class="km-notif__item"
                  :class="{ unread: !(n.read || readSet[n.id]) }"
                  @click="openItem(n)"
                >
                  <div class="km-notif__icon" :style="{background: colorFor(i)}">
                    {{ emojiFor(n) }}
                  </div>
                  <div class="km-notif__meta">
                    <div class="km-notif__title">{{ cleanText(n.name) }}</div>
                    <div class="km-notif__text">{{ cleanText(n.text) }}</div>
                    <div class="km-notif__time">{{ formatTime(n.create_time) }}</div>
                  </div>
                  <span v-if="!readSet[n.id]" class="km-notif__dot"></span>
                </li>
              </ul>
            </div>

            <footer class="km-notif__foot" v-if="items.length">
              <button @click="viewAll">View all notices →</button>
            </footer>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'NotificationBell',
  data(){
    return {
      open: false,
      loading: false,
      items: [],
      readSet: {},   // id -> true
      lastFetchAt: 0,
      isMobile: typeof window !== 'undefined' && window.innerWidth < 720
    };
  },
  computed: {
    show(){
      if(!localStorage.getItem('token')) return false;
      const n = this.$route && this.$route.name;
      const HIDE = ['landing','login','register','login-legacy','register-legacy',
                    'chat-thread','support'];
      return HIDE.indexOf(n) === -1;
    },
    isLotteryPage(){
      return (this.$route && this.$route.name) === 'Lottery';
    },
    unread(){
      // Prefer server-side `read` flag; fall back to local readSet for older payloads
      return this.items.filter(n => !(n.read || this.readSet[n.id])).length;
    },
    unreadLabel(){
      return this.unread > 9 ? '9+' : String(this.unread);
    }
  },
  watch: {
    show(v){ if(v && this.items.length === 0) this.fetch(); }
  },
  mounted(){
    // Read state is now server-side — any old localStorage blob can be cleared.
    try { localStorage.removeItem('km_notif_read'); } catch(e){ /* ignore */ }
    if(this.show) this.fetch();
    this.$options._resize = () => { this.isMobile = window.innerWidth < 720; };
    window.addEventListener('resize', this.$options._resize);
    // refresh silently every 60s
    this.$options._poll = setInterval(() => { if(this.show && !this.open) this.fetch(true); }, 60000);
  },
  beforeDestroy(){
    if(this.$options._resize) window.removeEventListener('resize', this.$options._resize);
    if(this.$options._poll) clearInterval(this.$options._poll);
  },
  methods: {
    toggle(){ this.open = !this.open; if(this.open) this.fetch(); },
    close(){ this.open = false; },
    fetch(silent){
      if(!silent){ this.loading = true; }
      this.$http({ method: 'get', url: 'sys_get_notice_list' }).then(res => {
        this.loading = false;
        const raw = (res && res.data) || {};
        const list = Array.isArray(raw) ? raw : Object.values(raw || {});
        // Sort newest first if we have timestamps
        this.items = list.slice().sort((a, b) => {
          const ta = Number(a && a.create_time) || 0;
          const tb = Number(b && b.create_time) || 0;
          return tb - ta;
        });
        this.lastFetchAt = Date.now();
      }).catch(() => { this.loading = false; });
    },
    markAllRead(){
      // Optimistic UI — flip items to read, then persist to server
      this.items.forEach(n => { this.$set(n, 'read', true); });
      this.$http({ method: 'post', url: 'notice_mark_all_read' }).then(() => {
        this.$toast.success('Marked as read');
      });
    },
    openItem(n){
      // Mark this one read + open the Notice page for the full view
      if (n && n.id != null && !n.read){
        this.$set(n, 'read', true);
        this.$http({ method: 'post', url: 'notice_mark_read', data: { notice_id: n.id } });
      }
      this.open = false;
      this.$router.push('/Notice');
    },
    viewAll(){ this.open = false; this.$router.push('/Notice'); },
    cleanText(s){
      // Strip obvious HTML tags and control chars
      return String(s || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    },
    emojiFor(n){
      const t = (this.cleanText(n && n.name) + ' ' + this.cleanText(n && n.text)).toLowerCase();
      if(/(withdraw|deposit|transfer|pay|recharge|wallet)/.test(t)) return '💎';
      if(/(match|like|heart)/.test(t)) return '💖';
      if(/(message|chat|reply)/.test(t)) return '💬';
      if(/(reward|gift|bonus|prize)/.test(t)) return '🎁';
      if(/(verify|verified|approved|confirm)/.test(t)) return '✅';
      if(/(alert|warn|security|caution)/.test(t)) return '⚠️';
      return '🔔';
    },
    colorFor(i){
      const pals = [
        'linear-gradient(135deg,#ff6b9d,#ff4d8d)',
        'linear-gradient(135deg,#c084fc,#a020f0)',
        'linear-gradient(135deg,#60a5fa,#3b82f6)',
        'linear-gradient(135deg,#fbbf24,#f59e0b)',
        'linear-gradient(135deg,#34d399,#10b981)',
        'linear-gradient(135deg,#fda4af,#f43f5e)'
      ];
      return pals[i % pals.length];
    },
    formatTime(ts){
      if(!ts) return '';
      // ts might be a string, unix seconds, or ms — normalise
      let n = typeof ts === 'number' ? ts : Number(ts);
      if(isNaN(n) && typeof ts === 'string') return ts;
      if(n < 1e11) n = n * 1000; // seconds → ms
      const d = new Date(n);
      const now = Date.now();
      const diff = Math.max(0, Math.round((now - n) / 1000));
      if(diff < 60) return 'just now';
      if(diff < 3600) return Math.floor(diff / 60) + ' min ago';
      if(diff < 86400) return Math.floor(diff / 3600) + ' h ago';
      if(diff < 7 * 86400) return Math.floor(diff / 86400) + ' d ago';
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
  }
};
</script>

<style scoped>
.km-notif {
  position: fixed;
  top: calc(10px + env(safe-area-inset-top, 0)); right: 12px; z-index: 58;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
}

/* Bell button */
.km-notif__btn {
  position: relative;
  width: 40px; height: 40px; border-radius: 50%;
  background: #fff; border: 1px solid rgba(26,16,51,.08);
  display: flex; align-items: center; justify-content: center;
  color: #2a1a3f; cursor: pointer;
  box-shadow: 0 4px 14px rgba(26,16,51,.08);
  transition: transform .15s ease, border-color .2s ease, box-shadow .25s ease;
  -webkit-tap-highlight-color: transparent;
}
.km-notif__btn:hover { border-color: #ff4d8d; transform: translateY(-1px); box-shadow: 0 8px 18px rgba(255,77,141,.2); }
.km-notif__btn.active {
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; border-color: transparent;
  box-shadow: 0 10px 22px rgba(255,77,141,.4);
}
.km-notif__badge {
  position: absolute; top: -4px; right: -4px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 10px; font-weight: 800;
  min-width: 18px; height: 18px; padding: 0 5px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 999px; border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(255,77,141,.45);
}
.km-notif__btn.active .km-notif__badge { border-color: transparent; }

/* Backdrop */
.km-notif__mask {
  position: fixed; inset: 0; z-index: 1;
  background: rgba(26,16,51,.25);
  backdrop-filter: blur(6px);
  display: flex; justify-content: flex-end; align-items: flex-start;
  padding: 62px 12px 12px;
}

/* Dropdown panel (desktop) */
.km-notif__panel {
  position: relative;
  width: 380px; max-width: 100%; max-height: 70vh;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border: 1px solid rgba(255,77,141,.14);
  border-radius: 20px;
  box-shadow: 0 30px 60px rgba(160,32,240,.25), 0 10px 20px rgba(26,16,51,.08);
  display: flex; flex-direction: column; overflow: hidden;
  animation: knDrop .22s cubic-bezier(.2,.9,.3,1.2);
}
@keyframes knDrop {
  from { opacity: 0; transform: translateY(-8px) scale(.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

/* Mobile: full-width slide down */
.km-notif__panel--mobile {
  width: 100%; max-width: 100%;
  border-radius: 20px;
}

.km-notif__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px 12px;
  border-bottom: 1px solid rgba(255,77,141,.1);
}
.km-notif__head h3 {
  margin: 0; font-size: 16px; font-weight: 800;
  display: flex; align-items: center; gap: 8px;
}
.km-notif__count {
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 11px; font-weight: 800;
  padding: 2px 9px; border-radius: 999px;
}
.km-notif__head-actions { display: flex; align-items: center; gap: 8px; }
.km-notif__mark {
  background: none; border: none; cursor: pointer;
  color: #ff4d8d; font-weight: 700; font-size: 12px;
  font-family: inherit;
}
.km-notif__mark:hover { text-decoration: underline; }
.km-notif__close {
  width: 28px; height: 28px; border-radius: 50%; border: none;
  background: rgba(26,16,51,.06); color: #6b5c85;
  font-size: 14px; cursor: pointer;
}
.km-notif__close:hover { background: rgba(255,77,141,.1); color: #ff4d8d; }

.km-notif__body { flex: 1; overflow-y: auto; }

.km-notif__loading, .km-notif__empty {
  padding: 40px 20px; text-align: center;
  color: #6b5c85;
}
.km-notif__loader {
  width: 32px; height: 32px; margin: 0 auto 10px;
  border: 3px solid rgba(255,77,141,.2); border-top-color: #ff4d8d;
  border-radius: 50%; animation: knSpin .8s linear infinite;
}
@keyframes knSpin { to { transform: rotate(360deg); } }
.km-notif__empty-emoji { font-size: 40px; margin-bottom: 6px; }
.km-notif__empty h4 { margin: 0 0 4px; font-size: 15px; color: #2a1a3f; }
.km-notif__empty p { margin: 0; font-size: 12px; color: #8273a0; }

.km-notif__list { list-style: none; padding: 0; margin: 0; }
.km-notif__item {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 13px 18px;
  cursor: pointer; position: relative;
  border-bottom: 1px solid rgba(255,77,141,.06);
  transition: background .15s;
}
.km-notif__item:hover { background: rgba(255,77,141,.05); }
.km-notif__item:last-child { border-bottom: none; }
.km-notif__item.unread::before {
  content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
  background: linear-gradient(180deg, #ff4d8d, #a020f0);
}
.km-notif__icon {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 17px; color: #fff;
  box-shadow: 0 4px 10px rgba(26,16,51,.1);
}
.km-notif__meta { flex: 1; min-width: 0; }
.km-notif__title {
  font-size: 13px; font-weight: 800; color: #2a1a3f; letter-spacing: -.01em;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.km-notif__text {
  font-size: 12px; color: #5a4a78; margin-top: 2px; line-height: 1.45;
  overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.km-notif__time { font-size: 11px; color: #8d7fa6; margin-top: 4px; font-weight: 500; }
.km-notif__dot {
  position: absolute; top: 16px; right: 16px;
  width: 8px; height: 8px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  box-shadow: 0 0 8px rgba(255,77,141,.6);
  flex-shrink: 0;
}

.km-notif__foot {
  padding: 12px 18px; border-top: 1px solid rgba(255,77,141,.1);
  background: rgba(255,77,141,.03);
}
.km-notif__foot button {
  width: 100%; padding: 10px; border: none; border-radius: 10px;
  background: linear-gradient(135deg, rgba(255,77,141,.08), rgba(160,32,240,.08));
  color: #a020f0; font-weight: 700; font-size: 13px;
  font-family: inherit; cursor: pointer;
  transition: background .2s;
}
.km-notif__foot button:hover {
  background: linear-gradient(135deg, rgba(255,77,141,.15), rgba(160,32,240,.15));
}

/* Responsive placement */
/* Certify page: bell on the left so it does not cover the round-switcher */
.km-notif--lottery {
  left: 12px;
  right: auto;
  top: calc(10px + env(safe-area-inset-top, 0));
}
@media (min-width: 720px) { .km-notif { top: 14px; right: 72px; } .km-notif--lottery { left: 16px; right: auto; } }
/* Desktop (≥960px): the DesktopNav has its own bell in the header, so hide
   the floating one to avoid the duplicate the user flagged. */
@media (min-width: 960px) { .km-notif { display: none !important; } }
@media (max-width: 360px) { .km-notif { top: 8px; right: 8px; } .km-notif__btn { width: 36px; height: 36px; } }

/* Transitions */
.kn-fade-enter-active, .kn-fade-leave-active { transition: opacity .25s ease, transform .25s ease; }
.kn-fade-enter, .kn-fade-leave-to { opacity: 0; transform: scale(.6); }

.kn-drop-enter-active, .kn-drop-leave-active { transition: opacity .18s ease; }
.kn-drop-enter, .kn-drop-leave-to { opacity: 0; }
</style>
